/**
 * api/engagement.ts — the engagement module's calls (2026-09-23,
 * clinic-service engagement module; wire contract pinned 2026-09-23): the
 * deal-drawer Engagement tab, the ADMIN content library manager, and the
 * patient unlock lanes.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement tab +
 * content manager.
 *
 * All staff-authed. The READ is CLINICIAN + ADMIN; EVERY mutation is
 * ADMIN-only, server-enforced (`@Roles('ADMIN')`) — the portal gates the
 * affordances and the server is the control (R41). Staff lanes ride the
 * gateway's authed catch-all, so every path here is `/v1/clinic/…` even though
 * the pinned contract writes the service-local `/v1/deals/…` /
 * `/v1/engagement/…` forms. Local `[CONTRACTS GAP]` mirrors live in
 * api/types.ts.
 *
 * ⚠️ A COPY IS NOT A SEND — the floor rule this module's shape enforces:
 *  - `getEngagementWhatsAppMessage` only BUILDS the copy payload (it does
 *    refresh the unlock link's expiry server-side; that is a property of the
 *    link, not a contact record);
 *  - `recordEngagementLinkCopied` is the quiet copy mark, and it is a FLOOR,
 *    never a count — it returns void and swallows every failure, exactly as
 *    `recordPlanLinkCopied` does, so no caller can render a telemetry error at
 *    somebody who successfully copied a link;
 *  - only `logEngagementWhatsApp` puts a WhatsApp TOUCH on the timeline, and
 *    only because a human pressed "Log as sent on WhatsApp".
 */
import { get, post, request } from './client';
import { uploadPhoto } from './journey';
import type {
  DealEngagement,
  EngagementContentCreate,
  EngagementContentCreateResult,
  EngagementContentList,
  EngagementContentPatch,
  EngagementContentUpdated,
  EngagementRevokeResult,
  EngagementSendResult,
  EngagementWhatsAppLogged,
  EngagementWhatsAppMessage,
  MyEngagement,
  UnlockDownloadResult,
  UnlockResolveResult,
} from './types';

/** GET /v1/clinic/deals/:id/engagement — enrolment + touches + link + consent (CLINICIAN, ADMIN). */
export function getDealEngagement(dealId: string): Promise<DealEngagement> {
  return get(`/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement`, true);
}

/**
 * GET /v1/clinic/engagement/content — the ADMIN content library, published
 * first then draft then retired. TWO consumers now (2026-09-23): the send
 * picker (which never asks for retired — retired content is not sendable, and
 * it still filters to `published` itself) and the admin content-manager
 * screen, whose "show retired" toggle is the only caller of `includeRetired`.
 *
 * ⚠️ THE OPT-IN IS THE LITERAL STRING `'true'` OR THE PARAM IS NOT SENT AT
 * ALL — never a stringified boolean (the estate's `includeTest` rule: the
 * server pins the literal and only that widens). Omitting it keeps the read
 * byte-identical to the pre-toggle one.
 */
export function getEngagementContent(includeRetired = false): Promise<EngagementContentList> {
  return get(`/v1/clinic/engagement/content${includeRetired ? '?includeRetired=true' : ''}`, true);
}

/**
 * POST /v1/clinic/engagement/content (ADMIN) — create a library row and mint
 * its signed PUT (`{contentId, uploadUrl, maxBytes}`). `parseError`: the
 * server's R28 sweep 400s a title or short message naming a MEDICINE OR
 * TREATMENT — its POM library list plus two local terms, and no condition
 * vocabulary — with a complete sentence the dialog shows VERBATIM (these
 * guides' titles land on public unlock surfaces, so the upload boundary is
 * where the vocabulary is enforced). The refused term is never echoed back.
 */
export function createEngagementContent(body: EngagementContentCreate): Promise<EngagementContentCreateResult> {
  return post('/v1/clinic/engagement/content', body, { auth: true, parseError: true });
}

/**
 * PATCH /v1/clinic/engagement/content/:id (ADMIN) — edit the words, publish,
 * or retire. `parseError`: every refusal is a sentence the surface shows
 * VERBATIM — the publish-before-upload 409, the retired-never-republishes
 * 409, and the R28 sweep's 400.
 */
export function updateEngagementContent(
  contentId: string,
  body: EngagementContentPatch,
): Promise<EngagementContentUpdated> {
  return request(`/v1/clinic/engagement/content/${encodeURIComponent(contentId)}`, {
    method: 'PATCH',
    body,
    auth: true,
    parseError: true,
  });
}

/**
 * The engagement-guide PDF ceiling — the client-side pre-check's honest
 * limit, mirroring the byte range clinic-service binds into the signed PUT
 * (`1,10485760`). The PUT itself echoes the WIRE's `maxBytes` (the create's
 * 201 carries the signed grant's own ceiling); this constant stands in ONLY
 * where the wire gave null/absent — the stubbed-storage case, which signs no
 * PUT at all — so the message and the refusal cannot disagree.
 */
export const ENGAGEMENT_PDF_MAX_BYTES = 10 * 1024 * 1024;

/**
 * PUT the picked PDF to the create's signed `uploadUrl`. This is the SAME
 * grant→PUT seam the deal-note attachments ride (`uploadPhoto` owns the one
 * PUT discipline: Content-Type + the signed `x-goog-content-length-range`
 * echoed, or GCS 400s) — the create result just lacks the grant SHAPE, so it
 * is mapped onto it here rather than growing a second PUT implementation.
 *
 * `maxBytes` is the create wire's own ceiling and the header echoes IT when
 * present — the exact range the server signed, which a server-side bump
 * re-signs while a mirrored constant would keep 400ing every PUT. The
 * fallback to `ENGAGEMENT_PDF_MAX_BYTES` exists for a null/absent wire value
 * only: the stubbed-storage case (and a clinic build predating the field).
 */
export function uploadEngagementPdf(uploadUrl: string, file: File, maxBytes?: number | null): Promise<void> {
  return uploadPhoto({ stub: false, uploadUrl, maxBytes: maxBytes ?? ENGAGEMENT_PDF_MAX_BYTES }, file, 'application/pdf');
}

/**
 * POST /v1/clinic/deals/:id/engagement/send (ADMIN) — email the patient one
 * published piece's unlock link. `parseError`: every 409 this route makes
 * (consent absent/withdrawn, content not published, no address) is a complete
 * PHI-free sentence the dialog shows VERBATIM, with the dialog still open —
 * the close-as-lost lesson.
 */
export function sendEngagementContent(dealId: string, contentId: string): Promise<EngagementSendResult> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement/send`, { contentId }, { auth: true, parseError: true });
}

/**
 * POST /v1/clinic/deals/:id/engagement/whatsapp-message (ADMIN) — the copy
 * payload: `{message, url}`, copied as `${message}\n${url}`. `contentId` is
 * optional: absent means "the plain unlock message" (an automated step's row);
 * present means that content's short message. This route DOES refresh the
 * unlock link's expiry.
 */
export function getEngagementWhatsAppMessage(dealId: string, contentId?: string): Promise<EngagementWhatsAppMessage> {
  return post(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement/whatsapp-message`,
    contentId === undefined ? {} : { contentId },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/deals/:id/engagement/link-copied (ADMIN) — the quiet audit
 * mark behind a copy. ⚠️ THE ORDER IS THE CONTRACT: the value reaches the
 * clipboard FIRST and this fires after it, best-effort. Returns `void` (not a
 * Promise) and swallows every rejection in this ONE place, so a caller
 * structurally cannot await it, branch on it or render a toast off it — the
 * `recordPlanLinkCopied` discipline, verbatim. The count that results is a
 * FLOOR, never a count.
 */
export function recordEngagementLinkCopied(dealId: string): void {
  void post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement/link-copied`, {}, { auth: true }).catch(() => {
    /* Deliberately empty — there is no failure of this call that a person
       copying a link can or should act on. */
  });
}

/**
 * POST /v1/clinic/deals/:id/engagement/whatsapp-log (ADMIN) — "Log as sent on
 * WhatsApp": the one call that puts a WhatsApp TOUCH on the timeline. `note`
 * is optional, ≤500 (the server's zod cap; the dialog's field stops at it).
 * The 201 body is the LITERAL `{}` — the recorded touch rides the NEXT
 * engagement read, which is why the panel refreshes by re-reading and no
 * caller may reach for a touch on this response.
 */
export function logEngagementWhatsApp(
  dealId: string,
  body: { contentId?: string; note?: string },
): Promise<EngagementWhatsAppLogged> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement/whatsapp-log`, body, { auth: true, parseError: true });
}

/**
 * POST /v1/clinic/deals/:id/engagement/revoke-links (ADMIN) — the wrong-chat
 * kill switch: sweeps EVERY live unlock link on the deal (each send mints its
 * own hashed link, so one deal can hold several), answering 201
 * `{ revoked: <count stamped by this call> }` — 0 on a repeat, which is the
 * idempotent steady state and never an error. The next send mints fresh, so
 * nothing else on the record changes. `parseError`: the 404 ('Unknown deal')
 * is a complete sentence the dialog shows verbatim.
 */
export function revokeEngagementLinks(dealId: string): Promise<EngagementRevokeResult> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/engagement/revoke-links`, {}, { auth: true, parseError: true });
}

/* ── The PATIENT side (2026-09-23) ─────────────────────────────────────────
 *
 * Two PUBLIC token lanes and the self-scoped signed-in pair. The public
 * lanes' capability is the unlock token, which travels ONLY in the request
 * BODY — it arrives in the URL FRAGMENT (`/unlock#t=…`, read + scrubbed by
 * `lib/hashToken`) and the gateway 400s these lanes when any query string is
 * present. NO auth on the public pair: an authed call that 401s clears the
 * SHARED session, which would sign a patient out of another tab (the
 * public-safety-page lesson).
 *
 * The public POSTs answer 201 (Nest's POST default) — the contract's own
 * note says clients gate on `res.ok`, not a literal, and the shared client
 * does exactly that.
 */

/**
 * POST /v1/clinic/engagement/resolve — what this unlock link offers.
 * EVERY failure (unknown / expired / revoked / purged / forged token) is one
 * generic, non-enumerating 404 — the page renders ONE dead-link state for all
 * of them and never guesses which.
 */
export function resolveUnlock(token: string): Promise<UnlockResolveResult> {
  return post('/v1/clinic/engagement/resolve', { token });
}

/**
 * POST /v1/clinic/engagement/download — trade the token + contentId for the
 * ladder: a short-TTL signed read URL, `locked` (state re-derived server-side
 * on THIS call), or `unconfirmed` (orders unreachable — honest, retryable).
 * A bad token OR a contentId never sent is the same generic 404.
 */
export function downloadUnlockContent(token: string, contentId: string): Promise<UnlockDownloadResult> {
  return post('/v1/clinic/engagement/download', { token, contentId });
}

/**
 * GET /v1/clinic/engagement/mine — the signed-in patient's own unlock state
 * (@Roles('PATIENT'), self-scoped server-side from the verified principal —
 * R41; there is no id to send and none may be added). No binding / no deal →
 * `{state:'none', items:[]}`, and the dashboard card renders nothing.
 */
export function getMyEngagement(): Promise<MyEngagement> {
  return get('/v1/clinic/engagement/mine', true);
}

/**
 * POST /v1/clinic/engagement/mine/download — the same download ladder with
 * the SESSION as the credential; no token anywhere. Re-derived on this call,
 * exactly like the public lane.
 */
export function downloadMyEngagementContent(contentId: string): Promise<UnlockDownloadResult> {
  return post('/v1/clinic/engagement/mine/download', { contentId }, { auth: true });}
